import { useEffect, useRef } from 'react';
import type { Socket } from 'socket.io-client';
import { useAuthStore } from '@/features/auth';
import type { Notification } from '@/entities/notification';
import { toast, setToastHref } from '@/shared/ui';
import { acquireSocket, releaseSocket } from '@/shared/api';
import { usePendingApprovalsStore } from '@/features/approvals';
import { useMessengerUnreadStore } from '@/features/messenger';
import { notificationLink } from '../lib/notification-link';
import { useNotificationsStore } from '../model/notifications-store';
import { notificationsCache } from '../api/queries';
import { useNotificationSoundStore } from '../model/notification-sound-store';
import {
  installSoundUnlock,
  playMentionSound,
  playNotificationSound,
} from '../lib/notification-sounds';
import { splitNotificationTitle } from '../lib/notification-title';
import { NotificationEventIcon, notificationVisual } from '../lib/notification-icon';
import toastStyles from './NotificationToast.module.css';

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const userId = useAuthStore((s) => s.user?.id);
  const reset = useNotificationsStore((s) => s.reset);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    installSoundUnlock();
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !userId) {
      reset();
      usePendingApprovalsStore.getState().reset();
      return;
    }

    let cancelled = false;

    void usePendingApprovalsStore.getState().refresh();

    const onNotification = (payload: Record<string, unknown>) => {
      const n = payload as unknown as Notification;
      if (!n?.id || (!n.ticketId && !n.payload?.taskId && !n.payload?.sprintId && !n.payload?.chatId)) return;
      n.readAt = n.readAt ?? null;
      n.read = !!n.readAt;
      notificationsCache.prepend(n);
      useNotificationsStore.getState().noteIncoming();

      if (n.eventType.startsWith('approval_')) {
        void usePendingApprovalsStore.getState().refresh();
      }

      const isMention = n.eventType === 'mentioned' || n.eventType === 'task_mentioned' || n.eventType === 'chat_mentioned';
      const isVip = n.eventType === 'ticket_created' && n.payload?.vip === true;
      const isPriority = isMention || isVip;

      if (n.muted && !isPriority) return;

      const sound = useNotificationSoundStore.getState();
      if (isPriority) playMentionSound(sound.volume);
      else if (sound.enabled) playNotificationSound(n.eventType, sound.volume, n.payload);

      const activeTicketId = useNotificationsStore.getState().activeTicketId;
      if (n.eventType === 'message_sent' && n.ticketId === activeTicketId) return;
      if (n.eventType === 'chat_message' && n.payload?.chatId === useMessengerUnreadStore.getState().activeChatId) return;

      const { event, subject } = splitNotificationTitle(n);
      const { tone } = notificationVisual(n.eventType, n.payload);
      const time = new Date(n.createdAt).toLocaleTimeString('ru', { hour: '2-digit', minute: '2-digit' });
      const id = toast(event, {
        description: subject ? `${subject} · ${time}` : time,
        icon: <NotificationEventIcon eventType={n.eventType} payload={n.payload} size={18} />,
        classNames: {
          icon: toastStyles[`tone-${tone}`],
          title: toastStyles.line,
          description: toastStyles.line,
        },
        ...(isVip ? { duration: Infinity } : {}),
      });
      setToastHref(id, notificationLink(n, userId));
    };

    const onMarkedRead = (payload: { notificationIds?: unknown }) => {
      const ids = Array.isArray(payload?.notificationIds)
        ? payload.notificationIds.filter((v): v is string => typeof v === 'string')
        : [];
      if (ids.length === 0) return;
      notificationsCache.removeMany(ids);
      const gone = new Set(ids);
      const now = new Date().toISOString();
      useNotificationsStore.setState((s) => ({
        pageItems: s.pageItems.map((n) => (gone.has(n.id) && !n.readAt ? { ...n, readAt: now, read: true } : n)),
      }));
    };

    const refreshMessengerBadge = () => {
      if (useAuthStore.getState().hasPermission('view_tickets')) void useMessengerUnreadStore.getState().refresh();
    };

    const onConnect = () => {
      notificationsCache.resync();
      void usePendingApprovalsStore.getState().refresh();
      refreshMessengerBadge();
    };
    let chatTimer: number | null = null;
    const onChatActivity = () => {
      if (chatTimer) window.clearTimeout(chatTimer);
      chatTimer = window.setTimeout(refreshMessengerBadge, 500);
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') refreshMessengerBadge();
    };
    document.addEventListener('visibilitychange', onVisible);

    void acquireSocket().then((socket) => {
      if (cancelled || !socket) return;
      socketRef.current = socket;
      socket.on('notification', onNotification);
      socket.on('notifications_marked_read', onMarkedRead);
      socket.on('connect', onConnect);
      socket.on('chat_activity', onChatActivity);
    });

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      if (socketRef.current) {
        socketRef.current.off('notification', onNotification);
        socketRef.current.off('notifications_marked_read', onMarkedRead);
        socketRef.current.off('connect', onConnect);
        socketRef.current.off('chat_activity', onChatActivity);
        socketRef.current = null;
      }
      releaseSocket();
    };
  }, [isAuthenticated, userId, reset]);

  return <>{children}</>;
};
